// Nav chrome for LeagueLayout: the wordmark centred with a caret that opens
// the league switcher, and the profile icon on the right.
//
// It carried the league name rather than the mark until the redesign. The name
// is not lost: every league tab leads with its own title card, so the bar was
// repeating what the page already said, and spending the one place the product
// names itself on it. Matching HomeNavBar also means the bar does not change
// shape when you move between /home and a league.
//
// Deliberately not a large-title bar: a 34pt collapsing title read as an
// oversized page header rather than app chrome.
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Home } from 'lucide-react';
import ProfileDropdown from '../ProfileDropdown';
import LeagueSwitcherSheet from './LeagueSwitcherSheet';
import { useTheme } from '../../hooks/useTheme';
import { useAuth } from '../../context/AuthContext';
import TourButton from './TourButton';
import wordmarkOnLight from '../../assets/logo-wordmark-on-light.png';
import wordmarkOnDark from '../../assets/logo-wordmark-on-dark.png';

function LeagueNavBar() {
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const { theme } = useTheme();
  const navigate = useNavigate();
  // Signed out, which only happens in the public demo league: there are no
  // leagues to switch between and no profile, so Home leads back to the
  // demo Home and the profile slot becomes Sign up.
  const { currentUser } = useAuth();
  const visitor = !currentUser;

  return (
    <>
      <div style={{
        position: 'sticky', top: 0, zIndex: 40,
        // Tokens, not literals, so the bar darkens with the page it sits on.
        // The alpha lives in the token: an opaque fill loses the blur.
        backgroundColor: 'var(--color-chrome)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderBottom: '1px solid var(--color-chrome-border)',
        paddingTop: 'env(safe-area-inset-top)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}>
        {/* Equal side columns, so the wordmark sits on the true centre of the
            bar rather than the centre of whatever is left over. */}
        <div style={{
          display: 'grid', gridTemplateColumns: '88px 1fr 88px', alignItems: 'center',
          padding: '8px 16px', minHeight: 52,
        }}>
          {/* The way out of a league. The small logo mark used to sit here and
              do this, and swapping it for the wordmark in the centre left the
              league pages with no route back to /home at all: the wordmark
              opens the switcher, which moves between leagues rather than out
              of them. */}
          <div style={{ display: 'flex' }}>
          <button
            onClick={() => navigate(visitor ? '/demo' : '/home')}
            aria-label="Home"
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 44, height: 44, border: 'none', background: 'none',
              cursor: 'pointer', padding: 0, color: 'var(--color-text-strong)',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <Home size={21} strokeWidth={1.8} />
          </button>
          <TourButton />
          </div>

          <button
            onClick={() => { if (!visitor) setSwitcherOpen(true); }}
            aria-label={visitor ? undefined : "Switch league"}
            disabled={visitor}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              border: 'none', background: 'none', cursor: visitor ? 'default' : 'pointer',
              padding: '4px 0', minHeight: 44, margin: '0 auto', maxWidth: '100%',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            {/* Picked from the resolved theme rather than a <picture> media
                query, so it follows the appearance setting and not only the
                OS. The inline script in index.html sets the theme before React
                mounts, so this is right on the first paint. */}
            <img
              src={theme === 'dark' ? wordmarkOnDark : wordmarkOnLight}
              alt="Lineup CFB"
              style={{ height: 33, width: 'auto', objectFit: 'contain' }}
            />
            {!visitor && <ChevronDown size={15} color="var(--color-text-faint)" style={{ flexShrink: 0 }} />}
          </button>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            {visitor ? (
              <button
                data-tour="demo-signup"
                onClick={() => navigate('/signup')}
                style={{
                  border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
                  backgroundColor: 'var(--color-cta)', color: 'var(--color-cta-text)',
                  fontWeight: 700, fontSize: 13, padding: '8px 14px', borderRadius: 50,
                }}
              >
                Sign up
              </button>
            ) : (
              <ProfileDropdown variant="icon" />
            )}
          </div>
        </div>
      </div>

      <LeagueSwitcherSheet open={switcherOpen} onClose={() => setSwitcherOpen(false)} />
    </>
  );
}

export default LeagueNavBar;
